import React, { useState } from 'react';
import { Asset, SeriesAsset } from '../../queries/ats/titles/types/asset';
import { Source } from '../../queries/ats/titles/types/sources';
import { AvailableContext, useAvailableContexts } from './use-available-contexts';
import { useFocusedAsset } from './use-focused-asset';
import { InputContext, useInputContext } from './use-input-context';
import { useSelectedContext } from './use-selected-context';

interface DetailsContextValues {
  focusedAsset: Exclude<Asset, SeriesAsset>;
  source: Source;
  selectedContext: AvailableContext;
  selectedContextId: string;
  setSelectedContextId: (providerId: string) => void;
  isTransactionModalOpen: boolean;
  setIsTransactionModalOpen: (isOpen: boolean) => void;
}

export const DetailsContext = React.createContext<DetailsContextValues | null>(null);

export function useDetails(componentName: string) {
  const context = React.useContext(DetailsContext);

  if (context === null) {
    throw new Error(`${componentName} tried to use "useDetails" without being wrapped in a "DetailsContextProvider"`);
  }

  return context;
}

interface DetailsContextProviderProps {
  asset: Asset;
  children: React.ReactNode;
  inputContext?: InputContext;
}

export function DetailsContextProvider({ asset, children }: DetailsContextProviderProps) {
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);

  const inputContext = useInputContext();
  const availableContexts = useAvailableContexts(asset);

  const { selectedContext, selectedContextId, setSelectedContextId } = useSelectedContext(
    availableContexts,
    inputContext,
  );

  const focusedAsset = useFocusedAsset(asset, selectedContext, inputContext);
  const source = focusedAsset.sources[0];

  return (
    <DetailsContext.Provider
      value={{
        focusedAsset,
        source,
        selectedContext,
        selectedContextId,
        setSelectedContextId,
        isTransactionModalOpen,
        setIsTransactionModalOpen,
      }}
    >
      {children}
    </DetailsContext.Provider>
  );
}
